import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import {
  HYDRATION_SYNC_CHANNEL,
  HYDRATION_SYNC_STORAGE_KEY,
  refetchHydrationData,
} from '@/lib/hydrationSync';

interface UseHydrationSyncOptions {
  /** Show toast when water is logged from a notification */
  showNotificationToasts?: boolean;
  /** Component name for logging */
  label?: string;
}

/**
 * Keeps hydration data in sync across:
 * - Notification/service worker water logs (same device)
 * - App resume from background (mobile + desktop)
 * - Cross-tab updates on the same device
 * - Cross-device updates when the user returns to the app
 */
export function useHydrationSync(options: UseHydrationSyncOptions = {}) {
  const { showNotificationToasts = false, label = 'HydrationSync' } = options;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    const refetch = (reason: string, skipDebounce = false) =>
      refetchHydrationData(queryClient, `${label}: ${reason}`, { skipDebounce });

    const showWaterLoggedToast = (volume: number) => {
      if (!showNotificationToasts) return;
      toast({
        title: 'Water Logged! 🎉',
        description: `Successfully logged ${volume}ml.`,
      });
    };

    const handleWaterLogged = async (event: Event) => {
      const customEvent = event as CustomEvent<{ volume?: number }>;
      console.log(`💧 ${label}: waterLogged event received`, customEvent.detail);
      await refetch('waterLogged event', true);
      showWaterLoggedToast(customEvent.detail?.volume ?? 0);
    };

    const handleServiceWorkerMessage = async (event: MessageEvent) => {
      if (event.data?.type !== 'WATER_LOGGED') return;
      console.log(`💧 ${label}: service worker water log`, event.data);
      await refetch('service worker water logged', true);
      showWaterLoggedToast(event.data.volume ?? 0);
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log(`👁️ ${label}: app became visible`);
        await refetch('visibility change');
      }
    };

    const handleWindowFocus = async () => {
      console.log(`🎯 ${label}: window gained focus`);
      await refetch('window focus');
    };

    // pageshow fires when returning from bfcache on iOS Safari
    const handlePageShow = async (event: PageTransitionEvent) => {
      if (event.persisted) {
        console.log(`📱 ${label}: page restored from bfcache`);
        await refetch('pageshow bfcache');
      }
    };

    const handleCrossTabSync = async (source: string) => {
      console.log(`📡 ${label}: cross-tab hydration update from ${source}`);
      await refetch(`cross-tab sync (${source})`, true);
    };

    const broadcastChannel =
      typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(HYDRATION_SYNC_CHANNEL) : null;

    const handleBroadcastMessage = (event: MessageEvent) => {
      if (event.data?.source) {
        void handleCrossTabSync(event.data.source);
      }
    };

    const handleStorageSync = (event: StorageEvent) => {
      if (event.key !== HYDRATION_SYNC_STORAGE_KEY || !event.newValue) return;
      try {
        const payload = JSON.parse(event.newValue) as { source?: string };
        void handleCrossTabSync(payload.source ?? 'storage');
      } catch {
        void handleCrossTabSync('storage');
      }
    };

    window.addEventListener('waterLogged', handleWaterLogged);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('storage', handleStorageSync);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    if (broadcastChannel) {
      broadcastChannel.addEventListener('message', handleBroadcastMessage);
    }

    return () => {
      window.removeEventListener('waterLogged', handleWaterLogged);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('storage', handleStorageSync);

      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }

      broadcastChannel?.removeEventListener('message', handleBroadcastMessage);
      broadcastChannel?.close();
    };
  }, [queryClient, toast, showNotificationToasts, label]);
}
